import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { formatMoney } from '../utils/format';
import { demoJackpot } from '../dev/demo';

// Monthly jackpot: the pot is only lost-bet money. A $100 ticket (1 a month) gets you in the draw.
// On the 1st (Montréal time) the server draws 3 ticket holders: 60 / 30 / 10 %.
// The first jackpot is October 2026 (same rule as jackpot_month() in the database).
const TICKET = 100;
export function jackpotMonth(d = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto', year: 'numeric', month: '2-digit' }).formatToParts(d);
  const key = `${parts.find((p) => p.type === 'year').value}-${parts.find((p) => p.type === 'month').value}`;
  return key < '2026-10' ? '2026-10' : key;
}
const drawDate = (month, lang) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(m === 12 ? y + 1 : y, m === 12 ? 0 : m, 1, 12));
  return d.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { month: 'long', day: 'numeric', timeZone: 'UTC' });
};

export default function Jackpot({ lang = 'en', profile, onBought }) {
  const t = lang === 'fr' ? TR.fr : TR.en;
  const month = jackpotMonth();
  const [pot, setPot] = useState(null);
  const [tickets, setTickets] = useState(0);
  const [mine, setMine] = useState(false);
  const [last, setLast] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const demo = demoJackpot(profile);   // localhost/?demo: a filled pot + you won last month
    if (demo) { setPot(demo.pot); setTickets(demo.tickets); setMine(true); setLast(demo.last); return; }
    const [{ data: j }, { data: tk }, { data: prev }] = await Promise.all([
      supabase.from('jackpots').select('pot').eq('month', month).maybeSingle(),
      supabase.from('jackpot_tickets').select('user_id').eq('month', month),
      supabase.from('jackpots').select('month, pot, winners').not('drawn_at', 'is', null).order('month', { ascending: false }).limit(1),
    ]);
    setPot(j ? Number(j.pot) : 0);
    setTickets(tk ? tk.length : 0);
    setMine(!!(tk && profile?.id && tk.some((x) => x.user_id === profile.id)));
    setLast(prev && prev[0] && (prev[0].winners || []).length ? prev[0] : null);
  }, [month, profile?.id]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60 * 1000);
    return () => clearInterval(id);
  }, [load]);
  // a bet just settled (balance moved): the pot may have grown
  useEffect(() => { load(); }, [profile?.balance, load]);

  const buy = async () => {
    if (!confirm) { setConfirm(true); setMsg(''); return; }
    setBusy(true);
    const { data, error } = await supabase.rpc('buy_jackpot_ticket');
    setBusy(false);
    setConfirm(false);
    if (error || !data?.success) {
      setMsg(data?.error === 'funds' ? t.funds : data?.error === 'already' ? t.already : t.failed);
      return;
    }
    setMsg('');
    onBought?.();
    load();
  };

  const iWonLast = last && profile?.id && last.winners.find((w) => w.user_id === profile.id);

  return (
    <div className="jp-card">
      <div className="jp-top">
        <span className="jp-label">{t.title}</span>
        <span className="jp-draw">{t.draw} {drawDate(month, lang)}</span>
      </div>
      <b className="jp-pot">{pot == null ? '—' : formatMoney(pot, lang)}</b>
      <span className="jp-sub">{tickets} {tickets === 1 ? t.ticket : t.tickets} · {t.split}</span>

      {mine ? (
        <div className="jp-in">{t.youreIn}</div>
      ) : (
        <button type="button" className={`jp-buy${confirm ? ' confirm' : ''}`} onClick={buy} disabled={busy}>
          {confirm ? t.confirm : t.buy}
        </button>
      )}
      {confirm && !busy && <button type="button" className="jp-cancel" onClick={() => setConfirm(false)}>{t.cancel}</button>}
      {msg && <span className="jp-msg">{msg}</span>}

      {last && (
        <div className="jp-last">
          <span className="jp-last-title">{iWonLast ? t.youWon(formatMoney(iWonLast.amount, lang)) : t.lastWinners}</span>
          {last.winners.map((w) => (
            <span key={w.place} className="jp-winner"><i>{w.place}</i>{w.username}<b>{formatMoney(w.amount, lang)}</b></span>
          ))}
        </div>
      )}
    </div>
  );
}

const TR = {
  en: {
    title: 'Monthly jackpot', draw: 'Draw', ticket: 'ticket', tickets: 'tickets', split: '3 winners 60% / 30% / 10%',
    buy: `Buy a ticket · $${TICKET}`, confirm: `Confirm · $${TICKET}`, cancel: 'Cancel',
    youreIn: 'You’re in this month’s draw', funds: `You need $${TICKET} to buy a ticket.`, already: 'You already have a ticket this month.',
    failed: 'Couldn’t buy the ticket. Try again.', lastWinners: 'Last month’s winners', youWon: (x) => `You won ${x} last month!`,
  },
  fr: {
    title: 'Gros lot du mois', draw: 'Tirage le', ticket: 'billet', tickets: 'billets', split: '3 gagnants 60 % / 30 % / 10 %',
    buy: `Acheter un billet · ${TICKET} $`, confirm: `Confirmer · ${TICKET} $`, cancel: 'Annuler',
    youreIn: 'Tu participes au tirage du mois', funds: `Il te faut ${TICKET} $ pour acheter un billet.`, already: 'Tu as déjà un billet ce mois-ci.',
    failed: 'Achat impossible. Réessaie.', lastWinners: 'Gagnants du mois dernier', youWon: (x) => `Tu as gagné ${x} le mois dernier!`,
  },
};
